import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronRight, FileText, CheckCircle, Send, Info } from 'lucide-react';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import axios from 'axios';
import { toast } from 'sonner';
import InteractiveTankDesigner, { captureTankDesignAsPng } from '../components/InteractiveTankDesigner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpecialtankPage = () => {
  const { language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [volume, setVolume] = useState('');
  const [material, setMaterial] = useState('Stål');
  const [pressureClass, setPressureClass] = useState('PN6');
  const [insulation, setInsulation] = useState('100mm PUR-isolering');
  const [removableInsulation, setRemovableInsulation] = useState(false);
  const [noInsulation, setNoInsulation] = useState(false);
  const [skiktplat, setSkiktplat] = useState(false);
  const [styrror, setStyrror] = useState(false);
  
  const [connections, setConnections] = useState([]);
  
  const [comments, setComments] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [orgNumber, setOrgNumber] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (connections.length < 2) {
      toast.error('Minst 2 anslutningar krävs');
      return;
    }
    setSubmitting(true);
    try {
      // Capture visual design as PNG (base64) before submit
      const designImage = await captureTankDesignAsPng();

      await axios.post(`${API}/specialtank-request`, {
        tankSpecs: {
          volume,
          material,
          pressureClass,
          insulation,
          removableInsulation,
          noInsulation,
          skiktplat,
          styrror,
          connections,
          comments
        },
        contact: { companyName, contactName, email, phone, orgNumber },
        designImage,
      });
      setSubmitted(true);
      toast.success('Förfrågan skickad!');
    } catch (error) {
      toast.error('Något gick fel');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white min-h-screen pt-24">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="text-center bg-gradient-to-br from-emerald-50 to-emerald-100 border-2 border-emerald-200 p-12">
            <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-stone-900 mb-4">Tack för din förfrågan!</h1>
            <p className="text-stone-600 mb-8">Vi återkommer inom 1-2 arbetsdagar med offert.</p>
            <Link to="/produkter">
              <Button className="bg-[#321a27] text-white">Tillbaka till produkter</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen pt-24">
      <SEO 
        title="Designa Specialtank | Flowingo" 
        description="Skräddarsy din ackumulatortank" 
        language={language} 
        schema={[generateBreadcrumbSchema([
          {name:'Hem',url:'https://flowingo.se'},
          {name:'Dimensionering',url:'https://flowingo.se/dimensionering'},
          {name:'Designa Specialtank',url:'https://flowingo.se/dimensionering/specialtank'}
        ])]} 
      />
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-8">
          <Link to="/">Hem</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/dimensionering">Dimensionering</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">Designa Specialtank</span>
        </nav>

        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 px-4 py-2 rounded-full mb-6">
            <FileText className="w-4 h-4 text-purple-700" />
            <span className="text-sm font-medium text-purple-700">Kundanpassad</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-6">Designa din specialtank</h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto">Beställ en ackumulatortank anpassad efter dina exakta behov.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Tank Specification */}
          <div className="bg-stone-50 border border-stone-200 p-8">
            <h2 className="text-2xl font-bold mb-6">Tankspecifikation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label>Volym (liter) *</Label>
                <Select value={volume} onValueChange={setVolume}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Välj volym..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {[300,500,750,1000,1500,2000,2500,3000,4000,5000,6000,7500,10000].map(v => 
                      <SelectItem key={v} value={String(v)}>{v}L</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Material *</Label>
                <Select value={material} onValueChange={setMaterial}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Stål">Stål</SelectItem>
                    <SelectItem value="Rostfritt stål">Rostfritt stål</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tryckklass *</Label>
                <Select value={pressureClass} onValueChange={setPressureClass}>
                  <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="PN3">PN3</SelectItem>
                    <SelectItem value="PN6">PN6</SelectItem>
                    <SelectItem value="PN10">PN10</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 bg-white border p-3 cursor-pointer">
                  <input type="checkbox" checked={skiktplat} onChange={(e) => setSkiktplat(e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Skiktplåt</span>
                </label>
                <label className="flex items-center gap-2 bg-white border p-3 cursor-pointer">
                  <input type="checkbox" checked={styrror} onChange={(e) => setStyrror(e.target.checked)} className="w-4 h-4" />
                  <span className="text-sm">Styrrör</span>
                </label>
              </div>
            </div>
          </div>

          {/* Interactive Connections Designer */}
          <div className="bg-stone-50 border border-stone-200 p-8">
            <h2 className="text-2xl font-bold mb-2">Anslutningar – designa visuellt</h2>
            <p className="text-stone-600 mb-6 text-sm">
              Klicka direkt på tankvyn för att placera dina anslutningar. Använd <strong>sidvyn</strong> för höjd och <strong>toppvyn</strong> för vinkel. Dra markörer för att flytta dem. Minst 2 anslutningar krävs.
            </p>

            <InteractiveTankDesigner
              connections={connections}
              setConnections={setConnections}
            />
          </div>

          {/* Guide Text */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
            <div className="flex gap-4">
              <Info className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-stone-900 mb-3">Guide för anslutningar</h3>
                <p className="text-stone-700 text-sm mb-2">
                  <strong>Placering av gängad huvudanslutning:</strong> Märk med G och önskad dimension (Exempelvis G50).
                </p>
                <p className="text-stone-700 text-sm mb-2">
                  <strong>Placering av flänsad huvudanslutning:</strong> Märk med F och önskad dimension (Exempelvis F100).
                </p>
                <p className="text-stone-700 text-sm mb-2">
                  <strong>Placering av svetsad huvudanslutning:</strong> Märk med S och önskad dimension (Exempelvis S80).
                </p>
                <p className="text-stone-700 text-sm mb-2">
                  <strong>Placering av varmvattenslinga:</strong> Märk med V samt önskad effekt (Om du kan) (Exempelvis V150kW).
                </p>
                <p className="text-stone-700 text-sm mb-3">
                  <strong>Placering av elpatron:</strong> Märk med E samt önskad effekt (Exempelvis E9kW).
                </p>
                <p className="text-stone-700 text-sm pt-3 border-t border-blue-200">
                  <strong>Avluftning och dränering:</strong> Kommer som standard, G 1".
                </p>
              </div>
            </div>
          </div>

          {/* Insulation */}
          <div className="bg-stone-50 border border-stone-200 p-8">
            <h2 className="text-2xl font-bold mb-6">Isolering</h2>
            <div className="mb-6">
              <Label>Typ</Label>
              <Select value={insulation} onValueChange={setInsulation} disabled={noInsulation}>
                <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="100mm PUR-isolering">100mm PUR (Polyuretan)</SelectItem>
                  <SelectItem value="13mm armaflex isolering">13mm armaflex (Cellgummi)</SelectItem>
                  <SelectItem value="19mm armaflex isolering">19mm armaflex</SelectItem>
                  <SelectItem value="25mm armaflex isolering">25mm armaflex</SelectItem>
                  <SelectItem value="32mm armaflex isolering">32mm armaflex</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 bg-white border p-4 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={removableInsulation} 
                  onChange={(e) => setRemovableInsulation(e.target.checked)} 
                  disabled={noInsulation} 
                  className="w-5 h-5" 
                />
                <span>Avtagbar isolering</span>
              </label>
              <label className="flex items-center gap-3 bg-white border p-4 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={noInsulation} 
                  onChange={(e) => { setNoInsulation(e.target.checked); if(e.target.checked) setRemovableInsulation(false); }} 
                  className="w-5 h-5" 
                />
                <span>Ingen isolering</span>
              </label>
            </div>
          </div>

          {/* Additional Comments */}
          <div className="bg-stone-50 border border-stone-200 p-8">
            <h2 className="text-2xl font-bold mb-6">Övriga önskemål</h2>
            <Textarea 
              value={comments} 
              onChange={(e) => setComments(e.target.value)} 
              placeholder="Beskriv specialkrav, tillval, specifika mått från golv till anslutning eller övrig information..." 
              rows={4} 
              className="bg-white" 
            />
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-[#321a27] to-[#4a2d3d] text-white p-8">
            <h2 className="text-2xl font-bold mb-6">Kontaktuppgifter</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white/90">Företag *</Label>
                <Input 
                  value={companyName} 
                  onChange={(e) => setCompanyName(e.target.value)} 
                  required 
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50" 
                />
              </div>
              <div>
                <Label className="text-white/90">Kontaktperson *</Label>
                <Input 
                  value={contactName} 
                  onChange={(e) => setContactName(e.target.value)} 
                  required 
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50" 
                />
              </div>
              <div>
                <Label className="text-white/90">E-post *</Label>
                <Input 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50" 
                />
              </div>
              <div>
                <Label className="text-white/90">Telefon *</Label>
                <Input 
                  type="tel" 
                  value={phone} 
                  onChange={(e) => setPhone(e.target.value)} 
                  required 
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50" 
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-white/90">Org.nr</Label>
                <Input 
                  value={orgNumber} 
                  onChange={(e) => setOrgNumber(e.target.value)} 
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50" 
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col items-center gap-4 py-8">
            <Button 
              type="submit" 
              disabled={submitting} 
              className="bg-[#321a27] hover:bg-[#4a2d3d] text-white px-12 py-6 text-lg"
            >
              {submitting ? 'Skickar...' : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Skicka förfrågan
                </>
              )}
            </Button>
            <p className="text-sm text-stone-500 text-center">Vi återkommer inom 1-2 arbetsdagar</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SpecialtankPage;
